import { useRef } from 'react';
import { Box, Typography, IconButton, useTheme } from '@mui/material';
import { IconX, IconArrowsDiagonal } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import DocsBody from './DocsBody.jsx';
import { useDocsDock, DOCK_WIDTH } from '../../contexts/DocsDockContext.jsx';

/**
 * Dockable documentation reference panel (#1064). A fixed right-side column the
 * user opens from the user menu to read the docs while they keep working; the
 * app shell shifts its content left by DOCK_WIDTH on md+ (see App.jsx). On small
 * screens it covers the viewport as a full-width reader.
 *
 * Always mounted so the open/close transform animates; pointer events are off
 * while closed. Reuses the shared DocsBody (variant="panel") - the same content
 * as the full /docs page - with the panel as the scrollspy root.
 */
const DocsDock = () => {
  const theme = useTheme();
  const { ds, palette } = theme;
  const { open, closeDock } = useDocsDock();
  const navigate = useNavigate();
  const scrollRef = useRef(null);

  const openFullPage = () => {
    closeDock();
    navigate('/docs');
  };

  return (
    <Box
      role="complementary"
      aria-label="Knowledge Base"
      aria-hidden={!open}
      sx={{
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        width: { xs: '100%', sm: 420, md: DOCK_WIDTH },
        maxWidth: '100%',
        zIndex: theme.zIndex.appBar + 2,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: palette.surface.overlay,
        borderLeft: `1px solid ${palette.overlay.border}`,
        boxShadow: palette.overlay.shadow,
        transform: open ? 'translateX(0)' : 'translateX(100%)',
        transition: `transform ${ds.motion.base} ${ds.motion.easeOut}`,
        pointerEvents: open ? 'auto' : 'none',
        '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
      }}
    >
      {/* Render the (heavy) content only while open: keeps the closed panel out
          of the DOM text + a11y tree, and the outer box still animates. */}
      {open && (
        <>
          <Box
            component="header"
            sx={{
              flex: '0 0 auto',
              display: 'flex',
              alignItems: 'center',
              gap: `${ds.space[3]}px`,
              padding: `${ds.space[5]}px ${ds.space[6]}px`,
              borderBottom: `1px solid ${palette.divider}`,
            }}
          >
            <Typography
              sx={{
                flex: 1,
                minWidth: 0,
                fontSize: `${ds.font.sectionTitle}px`,
                fontWeight: ds.font.weightMedium,
                color: palette.text.primary,
              }}
            >
              Knowledge Base
            </Typography>
            <IconButton size="small" onClick={openFullPage} aria-label="Open knowledge base full page">
              <IconArrowsDiagonal size={18} />
            </IconButton>
            <IconButton size="small" onClick={closeDock} aria-label="Close knowledge base">
              <IconX size={18} />
            </IconButton>
          </Box>

          <Box
            ref={scrollRef}
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              padding: `0 ${ds.space[6]}px ${ds.space[10]}px`,
            }}
          >
            <DocsBody variant="panel" scrollRef={scrollRef} />
          </Box>
        </>
      )}
    </Box>
  );
};

export default DocsDock;
